import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, take } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MuiScrollbarService {
  scrollPosition: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  scrolledToBottom: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  #safetyHeight = 10;

  constructor() {
    window.addEventListener('scroll', () => {
      this.scrolledToBottom.next(window.innerHeight + window.scrollY >= document.documentElement.scrollHeight);
      this.scrollPosition.next(window.pageYOffset);
    });
  }

  scrollToBottom(): void {
    window.scrollTo({
      left: 0,
      top: document.body.scrollHeight + this.#safetyHeight, // Scroll to bottom of scrollHeight plus some safety-height if something isn't ready
      behavior: 'smooth'
    });
  }

  scrollToYPosition(pos: number): void {
    window.scrollTo({
      left: 0,
      top: pos,
      behavior: 'smooth'
    });
  }

  /**
   * Function to activate scrolling to an element
   * @param elementID the ID of the element to scroll to, if empty nothing happens
   * */
  scrollToElement(elementID: string): void {
    // Start scrolling only if elementID is present
    if (elementID) {
      this.waitForImagesToLoad()
        .pipe(
          filter((loaded) => loaded === true),
          take(1)
        )
        .subscribe(() => {
          // search for the element with the specified ID
          if (document.getElementById(elementID)) {
            const element = document.getElementById(elementID);

            if (element) {
              // if element is bigger than the screen, scroll to the beginning of the element plus an offset to make it look more beautiful
              if (element.getBoundingClientRect().height * 1.1 >= window.innerHeight) {
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.scrollY - window.innerHeight / 20;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
              } else {
                // when there's enough room, scroll the element into the center of the screen
                element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
              }
            }
          }
        });
    }
  }

  waitForImagesToLoad(): Observable<boolean> {
    const images = document.querySelectorAll('img');
    let counter = 0;

    const allImagesLoaded = new BehaviorSubject<boolean>(false);

    if (images.length === 0) {
      allImagesLoaded.next(true);
    } else {
      for (let i = 0; i < images.length; i++) {
        images[i].addEventListener(
          'load',
          () => {
            if (++counter === images.length) {
              //all images loaded
              allImagesLoaded.next(true);
            }
          },
          false
        );
      }
    }

    return allImagesLoaded.asObservable();
  }
}
